import React, { ReactNode, useEffect, useState } from 'react';
import { ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { ApolloClient } from '@apollo/client';
import QueueLink from 'apollo-link-queue';

import { createApolloClient } from '../../../lib/createApolloClient';
import { parentUser, staffUser } from '../../../lib/services/auth.service';
import { SessionType } from '../../../lib/types/session.types';
import { SessionReporter } from '../../../lib/utils/session.reporter';
import ErrorPage from '../../_pages/_boilerplate/ErrorPage/error-page.container';
import LoadingPage from '../../_pages/_boilerplate/LoadingPage/loading-page.container';
import { useOnlineStatus, setIsQueueOpen } from '../../../lib/stores/connectivity.store';

type BuiltClient = { client: ApolloClient<NormalizedCacheObject>; queueLink?: QueueLink };

// Cache the built client per tree so returning to an app reuses its warm cache
// instead of rebuilding (which would drop in-memory cache and refetch everything).
const builtClients: Partial<Record<SessionType, Promise<BuiltClient>>> = {};

const userForTree = (tree: SessionType) => (tree === SessionType.PARENT ? parentUser() : staffUser());

const obtainClient = (tree: SessionType): Promise<BuiltClient> => {
  if (!builtClients[tree]) builtClients[tree] = createApolloClient(tree, userForTree(tree));
  return builtClients[tree] as Promise<BuiltClient>;
};

// One ApolloClient per session tree, built lazily on mount; crossing the
// staff/parent boundary swaps providers (see CLAUDE.md "Parent/staff cache isolation").
export const AppApolloProvider: React.FC<{ tree: SessionType; children: ReactNode }> = ({ tree, children }) => {
  const online = useOnlineStatus();
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();
  const [queueLink, setQueueLink] = useState<QueueLink>();
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    obtainClient(tree)
      .then((built) => {
        if (cancelled) return;
        // Re-point identity at the mounted app's user even when the client is
        // reused, so a tree switch reports the active session, not the prior one.
        SessionReporter.setApolloClient(built.client);
        const user = userForTree(tree);
        if (user) SessionReporter.identify({ _id: user._id, name: user.name, email: user.email });
        setClient(built.client);
        setQueueLink(built.queueLink);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('AppApolloProvider failed to build the Apollo client:', err);
        setError(true);
      });
    return () => { cancelled = true; };
  }, [tree]);

  // The staff queue opens/closes with connectivity; the parent client has none.
  useEffect(() => {
    if (!queueLink) return undefined;
    let openTimeout: any;
    if (online) {
      openTimeout = setTimeout(() => { queueLink.open(); setIsQueueOpen(true); }, 5 * 1000);
    } else {
      queueLink.close();
      setIsQueueOpen(false);
    }
    return () => clearTimeout(openTimeout);
  }, [online, queueLink]);

  if (error) return <ErrorPage />;
  return client ? <ApolloProvider client={client}>{children}</ApolloProvider> : <LoadingPage />;
};
